import {useEffect, useState} from 'react'
import {useQuery, type UseQueryResult} from '@tanstack/react-query'

import {PoliciesService} from 'services/api'
import {
  ApplicationOSType,
  BypassDataClient,
  BypassDataServer,
  BypassType,
  TrafficType,
} from 'interfaces'

export const GET_BYPASS_QUERY_KEY = 'getAllBypassData'

interface GetBypassQueryProps {
  policyName: string
}

interface GetBypassQueryResponse extends Omit<UseQueryResult, 'data'> {
  data: BypassDataClient
  onUpdateBypassData: (data: BypassDataClient) => void
}

export const useGetBypass = ({
  policyName,
}: GetBypassQueryProps): GetBypassQueryResponse => {
  const [bypassData, setBypassData] = useState<BypassDataClient>(
    {} as BypassDataClient,
  )

  const {data, isLoading, isSuccess, isError, error, ...rest} = useQuery({
    queryKey: [GET_BYPASS_QUERY_KEY, policyName],
    queryFn: () => PoliciesService.getBypass({policyName}),
    select: ({data}): BypassDataClient =>
      transformBypassServerDataToClientData(data),
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!isLoading && data) {
      setBypassData(data)
    }
  }, [isLoading, data])

  return {
    data: bypassData,
    onUpdateBypassData: setBypassData,
    isLoading: isLoading,
    isSuccess,
    isError,
    error,
    ...rest,
  }
}

const transformBypassServerDataToClientData = (
  data: BypassDataServer,
): BypassDataClient => {
  return {
    ...data,
    customDomainBypassList: data.customDomainBypassList.map(item => ({
      ...item,
      isEdit: false,
      isDefault: false,
      bypassType: BypassType.url,
    })),
    domainDefaultBypassList: data.domainDefaultBypassList.map(item => ({
      name: item,
      bypassType: BypassType.url,
      isDefault: true,
      traffic: data.domainIgnoreBypassList.includes(item)
        ? TrafficType.IGNORE
        : TrafficType.BYPASS,
      isEdit: false,
    })),
    customApplicationBypassList: [
      ...data.customApplicationBypassList.Mac.map(item => ({
        ...item,
        isEdit: false,
        bypassType: BypassType.app,
        os: ApplicationOSType.MAC,
      })),
      ...data.customApplicationBypassList.Windows.map(item => ({
        ...item,
        isEdit: false,
        bypassType: BypassType.app,
        os: ApplicationOSType.WINDOWS,
      })),
    ].sort(
      (a, b) =>
        new Date(b.updatedAt || 0).getTime() -
        new Date(a.updatedAt || 0).getTime(),
    ),
    applicationDefaultBypassList: [
      ...data.applicationDefaultBypassList.Mac.map(item => ({
        name: item,
        bypassType: BypassType.app,
        isDefault: true,
        os: ApplicationOSType.MAC,
        traffic: data.applicationIgnoreBypassList.Mac.includes(item)
          ? TrafficType.IGNORE
          : TrafficType.BYPASS,
        isEdit: false,
      })),
      ...data.applicationDefaultBypassList.Windows.map(item => ({
        name: item,
        bypassType: BypassType.app,
        isDefault: true,
        os: ApplicationOSType.WINDOWS,
        traffic: data.applicationIgnoreBypassList.Windows.includes(item)
          ? TrafficType.IGNORE
          : TrafficType.BYPASS,
        isEdit: false,
      })),
    ],
    applicationIgnoreBypassList: [
      ...data.applicationIgnoreBypassList.Mac.map(item => ({
        name: item,
        bypassType: BypassType.app,
        os: ApplicationOSType.MAC,
        isDefault: false,
        isEdit: false,
      })),
      ...data.applicationIgnoreBypassList.Windows.map(item => ({
        name: item,
        bypassType: BypassType.app,
        os: ApplicationOSType.WINDOWS,
        isDefault: false,
        isEdit: false,
      })),
    ],
  }
}
